import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useIsLandscape } from '../../hooks/useIsLandscape';
import {
  ADJUSTMENT_MENU,
  ADJUSTMENT_SPECS,
  CATEGORY_LABELS,
  type AdjustmentCategory,
  type AdjustmentKind,
  type AdjustmentMenuItem,
} from './registry';

interface AdjustmentsPickerProps {
  /** Called when the user picks a kind that has an implemented spec.
   *  Picker closes itself before invoking. */
  onPick: (kind: AdjustmentKind) => void;
  /** When true, the trigger renders disabled and clicking does
   *  nothing. Used when the active layer has no rasterizable
   *  pixels (text, shape, adjustment, or empty group) — surfaces
   *  the limitation up-front instead of letting the user click and
   *  see no dialog. Tooltip explains why. */
  disabled?: boolean;
}

export function AdjustmentsPicker({ onPick, disabled = false }: AdjustmentsPickerProps) {
  const [open, setOpen] = useState(false);
  const isLandscape = useIsLandscape();

  const grouped = useMemo(() => {
    const out = new Map<AdjustmentCategory, AdjustmentMenuItem[]>();
    for (const item of ADJUSTMENT_MENU) {
      const list = out.get(item.category) ?? [];
      list.push(item);
      out.set(item.category, list);
    }
    return out;
  }, []);

  const handlePick = (item: AdjustmentMenuItem) => {
    if (!ADJUSTMENT_SPECS[item.kind]) return; // unimplemented — ignore click
    setOpen(false);
    onPick(item.kind);
  };

  const enabledClass =
    'w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent';
  const disabledClass =
    'w-full px-3 py-1.5 text-xs rounded-md border border-border text-muted-foreground/50 cursor-not-allowed bg-muted/30';
  const triggerClass = disabled ? disabledClass : enabledClass;
  const disabledTitle = 'Select a layer with pixels (image, paint, or a group containing one)';

  if (disabled) {
    return (
      <button type="button" className={triggerClass} disabled title={disabledTitle}>
        Adjust Color/Tone&hellip;
      </button>
    );
  }

  if (isLandscape) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger className={triggerClass}>Adjust Color/Tone&hellip;</PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-2">
          <PickerBody grouped={grouped} onPick={handlePick} />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      <button type="button" className={triggerClass} onClick={() => setOpen(true)}>
        Adjust Color/Tone&hellip;
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-lg max-h-[80vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Adjust Color/Tone</SheetTitle>
          </SheetHeader>
          <div className="pt-2">
            <PickerBody grouped={grouped} onPick={handlePick} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function PickerBody({
  grouped,
  onPick,
}: {
  grouped: Map<AdjustmentCategory, AdjustmentMenuItem[]>;
  onPick: (item: AdjustmentMenuItem) => void;
}) {
  return (
    <div className="space-y-3">
      {Array.from(grouped.entries()).map(([category, items]) => (
        <div key={category}>
          <h5 className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wide mb-1 px-1">
            {CATEGORY_LABELS[category]}
          </h5>
          <ul className="space-y-0.5">
            {items.map((item) => {
              const available = Boolean(ADJUSTMENT_SPECS[item.kind]);
              return (
                <li key={item.kind}>
                  <button
                    type="button"
                    disabled={!available}
                    onClick={() => onPick(item)}
                    className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs rounded-md min-h-[32px] text-left transition-colors ${
                      available
                        ? 'text-foreground hover:bg-accent cursor-pointer'
                        : 'text-muted-foreground/60 cursor-not-allowed'
                    }`}
                  >
                    <span>{item.title}</span>
                    {!available && (
                      <span className="text-[10px] uppercase tracking-wide opacity-70">Soon</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
